/**
 * PortalLookupPage — `/portals/find` (no auth)
 *
 * Multi-portal recovery for dual-identity users. Enter your email →
 * CountBucks sends a single email listing every client portal that email
 * has access to, with one branded "Open portal" button per firm.
 *
 * We never disclose membership info in the API response — only via the
 * email itself — to prevent enumeration.
 */
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Mail, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PortalLookupPage() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email) { setError('Please enter your email.'); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/public/portal/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await r.json();
      if (!r.ok) {
        setError(data.detail || 'Lookup failed. Please try again.');
        return;
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-zinc-50 flex flex-col" data-testid="portal-lookup-page">
      <header className="border-b border-zinc-200">
        <div className="max-w-5xl mx-auto w-full px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center text-white font-bold">
              C
            </div>
            <span className="font-bold text-lg">CountBucks</span>
          </Link>
          <Link to="/login" className="text-sm font-medium text-emerald-600 hover:underline">
            Firm sign in
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-8">
            {!submitted ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="w-5 h-5 text-emerald-500" />
                  <h1 className="text-2xl font-bold text-zinc-900">Find your portal</h1>
                </div>
                <p className="text-sm text-zinc-600 mb-6">
                  Not sure which firm invited you? Enter your email and we'll send you a list of every client portal you have access to.
                </p>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="portal-lookup-form">
                  <div>
                    <Label htmlFor="lookup-email">Email</Label>
                    <Input
                      id="lookup-email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="mt-1"
                      placeholder="you@example.com"
                      data-testid="portal-lookup-email"
                    />
                  </div>
                  {error && (
                    <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="portal-lookup-error">
                      {error}
                    </div>
                  )}
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-11 text-base bg-emerald-500 hover:bg-emerald-600 text-white shadow-md hover:shadow-lg transition-shadow"
                    data-testid="portal-lookup-submit"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                    Email me my portals
                  </Button>
                </form>
              </>
            ) : (
              <div className="text-center" data-testid="portal-lookup-success">
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                </div>
                <h1 className="text-2xl font-bold text-zinc-900 mb-2">Check your inbox</h1>
                <p className="text-sm text-zinc-600">
                  If your email is linked to any client portals, we just sent you a list with one-click links to each.
                </p>
                <p className="text-xs text-zinc-400 mt-4">
                  Don't see it? Check your spam folder or wait a minute — Resend usually delivers in seconds.
                </p>
                <Button
                  onClick={() => { setSubmitted(false); setEmail(''); }}
                  variant="outline"
                  className="mt-6"
                  data-testid="portal-lookup-try-another"
                >
                  Try another email
                </Button>
              </div>
            )}
          </div>

          <p className="mt-6 text-center text-xs text-zinc-400">
            We never reveal which portals an email belongs to in the response —
            only in the email itself, so other people can't enumerate your access.
          </p>
        </div>
      </main>

      <footer className="border-t bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 text-xs text-zinc-500 text-center">
          Powered by{' '}
          <a href="https://countbucks.com" className="font-semibold text-zinc-700 hover:underline">CountBucks</a>
        </div>
      </footer>
    </div>
  );
}
